import React, { useEffect, useRef, useCallback } from "react";
import { size, isEmpty } from "lodash";
import { io } from "socket.io-client";
import { BASE_URL, MEDIA_URL } from "../../axios";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import playlistStore from "../../zustand/playlist.store";
import likedSongsStore from "../../zustand/likedSong.store";
import ignoredSongsStore from "../../zustand/ignoreSong.store";
import userStore from "../../zustand/user.store";
import SchedulesStore from "../../zustand/Schedule.store";
import { socketServices } from "../../services/socket.service";
import { doTokenRefresh, fetchCustomerData } from "../../actions/user.actions";
import { getToken, getRandomColor } from "../../helpers/generel.helpers";
import { shuffleArray } from "../../helpers/music.helpers";
import { useNavigate } from "react-router-dom";

const FADE_DURATION_SEC = 12;
const FADE_INTERVAL_MS = 50;
const FADE_STEPS = (FADE_DURATION_SEC * 1000) / FADE_INTERVAL_MS;

const MusicPlayerEngine = () => {
  const {
    songs, currentSongIndex, isPlaying, setIsPlaying,
    setCurrentSongIndex, playList, setSongs, setPlayList,
    setAudioRef1, setAudioRef2, setCurrentAudioRefIndex,
    toggleIsShuffle, isShuffle, searchedSong,
    setPlayListFromScoket, playSearchSong, beforeShuffleSongs,
    setBeforeShuffleSongs, isRepeat, toggleIsRepeat,
  } = musicPlayerStore((state) => state);

  const { playlists, addLikedSongToPlaylist, removeUnLikedSongFromPlaylist, setPlaylists } = playlistStore((state) => state);
  const { songs: likedSongs, addSong: addLikedSong, deleteSong: deleteLikedSong } = likedSongsStore();
  const { songs: ignoredSongs, addSong: addIgnoreSong, deleteSong: removeIgnoreSong } = ignoredSongsStore();
  const { isAuthenticated, userOffline } = userStore((state) => ({ isAuthenticated: state.isAuthenticated, userOffline: state.userOffline }));
  const { schedulesDone, markScheduleDone } = SchedulesStore((state) => state);

  const navigate = useNavigate();
  const audioRef1 = useRef(null);
  const audioRef2 = useRef(null);
  const bgAudioRef = useRef(null);
  const bgActiveRef = useRef(false);
  const currentAudioRefIndex = useRef(1);

  const getCurrentAudioRef = () => currentAudioRefIndex.current === 1 ? audioRef1 : audioRef2;
  const getOtherAudioRef = () => currentAudioRefIndex.current === 2 ? audioRef1 : audioRef2;

  // Web Audio API refs
  const audioCtxRef = useRef(null);
  const gainNode1Ref = useRef(null);
  const gainNode2Ref = useRef(null);
  const source1Ref = useRef(null);
  const source2Ref = useRef(null);

  const getGainForRef = (ref) => ref === audioRef1 ? gainNode1Ref : gainNode2Ref;
  const getCurrentGainNode = () => currentAudioRefIndex.current === 1 ? gainNode1Ref : gainNode2Ref;
  const getOtherGainNode = () => currentAudioRefIndex.current === 2 ? gainNode1Ref : gainNode2Ref;

  const songsRef = useRef(songs);
  const currentSongIdx = useRef(0);
  const songOffset = useRef(1);
  const isPlayingRef = useRef(false);
  const isShuffleRef = useRef(isShuffle);
  const isFadeOutStarted = useRef(false);
  const isPlaylistChanged = useRef(false);
  const isFirstSongOfPlaylist = useRef(true);
  const playlistsRef = useRef(playlists);
  const playListRef = useRef(playList);
  const scheduleDoneRef = useRef(schedulesDone);
  const searchedSongRef = useRef(searchedSong);
  const socketRef = useRef(null);
  const primed = useRef(false);
  const cycleTimerRef = useRef(null);
  const tokenTimerRef = useRef(null);
  const lastSwitchTs = useRef(0);
  const beforeShuffleSongsRef = useRef([]);
  const isRepeatRef = useRef(false);

  // Fade interval refs
  const fadeInIntervalRef = useRef(null);
  const fadeOutIntervalRef = useRef(null);

  const findNextValidSong = useCallback((offset) => {
    const total = size(songsRef.current);
    if (total === 0) return 0;
    let next = (currentSongIdx.current + offset) % total;
    if (next < 0) next = total + next;
    let guard = 0;
    while (songsRef.current[next]?.ignore && guard < total) {
      next = (next + offset) % total;
      if (next < 0) next = total + next;
      guard++;
    }
    return next;
  }, []);

  // =================== WEB AUDIO API SETUP ===================

  const ensureAudioContext = useCallback(() => {
    if (audioCtxRef.current) {
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      return audioCtxRef.current;
    }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;

      const el1 = audioRef1.current;
      const el2 = audioRef2.current;
      if (el1 && !source1Ref.current) {
        source1Ref.current = ctx.createMediaElementSource(el1);
        gainNode1Ref.current = ctx.createGain();
        gainNode1Ref.current.gain.value = 1;
        source1Ref.current.connect(gainNode1Ref.current);
        gainNode1Ref.current.connect(ctx.destination);
      }
      if (el2 && !source2Ref.current) {
        source2Ref.current = ctx.createMediaElementSource(el2);
        gainNode2Ref.current = ctx.createGain();
        gainNode2Ref.current.gain.value = 0;
        source2Ref.current.connect(gainNode2Ref.current);
        gainNode2Ref.current.connect(ctx.destination);
      }
      return ctx;
    } catch (_) {
      return null;
    }
  }, []);

  // Set gain value on a GainNode (works on iOS)
  const setGainValue = (gainNodeRef, val) => {
    if (!gainNodeRef?.current) return;
    gainNodeRef.current.gain.value = Math.max(0, Math.min(1, val));
  };

  // Legacy setGain for global engine compatibility
  const setGain = (audioRef, val) => {
    const gnRef = getGainForRef(audioRef);
    setGainValue(gnRef, val);
  };

  const updateMediaSession = useCallback((song) => {
    if (!song || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: song.title || 'מוזיקה',
        artist: song.artist || '4Play Music',
        artwork: song.imageFileName
          ? [{ src: `${MEDIA_URL}/songs_images/${song.imageFileName}`, sizes: '96x96', type: 'image/jpeg' }]
          : [],
      });
      navigator.mediaSession.playbackState = 'playing';
    } catch (_) {}
  }, []);

  const safePlay = (el) => {
    if (!el) return;
    const ctx = ensureAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const attempt = () => {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          setTimeout(() => el.play().catch(() => {}), 300);
        });
      }
    };
    if (el.readyState >= 2) {
      attempt();
    } else {
      el.addEventListener("canplay", () => attempt(), { once: true });
      if (!el.src || el.src === window.location.href) return;
      el.load();
    }
  };

  const primeElements = useCallback(() => {
    if (primed.current) return;
    primed.current = true;
    ensureAudioContext();
    [audioRef1, audioRef2, bgAudioRef].forEach((ref) => {
      const el = ref.current;
      if (!el) return;
      const hadSrc = el.src && el.src !== "" && el.src !== window.location.href;
      if (!hadSrc) el.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => { el.pause(); }).catch(() => {});
      }
    });
  }, [ensureAudioContext]);

  // =================== CROSSFADE ===================

  const clearAllFades = useCallback(() => {
    if (fadeInIntervalRef.current) { clearInterval(fadeInIntervalRef.current); fadeInIntervalRef.current = null; }
    if (fadeOutIntervalRef.current) { clearInterval(fadeOutIntervalRef.current); fadeOutIntervalRef.current = null; }
  }, []);

  // Core crossfade animation — called after swapping audio refs and setting the new src
  const fadeElements = useCallback(() => {
    clearAllFades();

    const newGain = getCurrentGainNode();
    const oldGain = getOtherGainNode();
    const newEl = getCurrentAudioRef().current;
    const oldEl = getOtherAudioRef().current;

    if (document.hidden) {
      setGainValue(newGain, 1);
      setGainValue(oldGain, 0);
      if (oldEl && !oldEl.paused) oldEl.pause();
      if (newEl && isPlayingRef.current) safePlay(newEl);
      isFadeOutStarted.current = false;
      const nextIdx = findNextValidSong(1) % size(songsRef.current);
      const nextUrl = songsRef.current?.[nextIdx]?.url;
      if (nextUrl && oldEl) oldEl.src = nextUrl;
      return;
    }

    setGainValue(newGain, 0);
    if (newEl && isPlayingRef.current) safePlay(newEl);

    let fadeStep = 0;
    fadeInIntervalRef.current = setInterval(() => {
      fadeStep++;
      const progress = Math.min(fadeStep / FADE_STEPS, 1);
      setGainValue(newGain, progress);
      if (progress >= 1) {
        clearInterval(fadeInIntervalRef.current);
        fadeInIntervalRef.current = null;
        isFadeOutStarted.current = false;
        const nextIdx = findNextValidSong(1) % size(songsRef.current);
        const nextUrl = songsRef.current?.[nextIdx]?.url;
        if (nextUrl && oldEl) oldEl.src = nextUrl;
      }
    }, FADE_INTERVAL_MS);

    const oldStartGain = oldGain?.current?.gain?.value ?? 1;
    let fadeOutStep = 0;
    fadeOutIntervalRef.current = setInterval(() => {
      fadeOutStep++;
      const progress = Math.min(fadeOutStep / FADE_STEPS, 1);
      setGainValue(oldGain, oldStartGain * (1 - progress));
      if (progress >= 1) {
        clearInterval(fadeOutIntervalRef.current);
        fadeOutIntervalRef.current = null;
        if (oldEl && !oldEl.paused) oldEl.pause();
      }
    }, FADE_INTERVAL_MS);
  }, [clearAllFades, findNextValidSong]);

  const startCrossfade = useCallback(() => {
    currentAudioRefIndex.current = (currentAudioRefIndex.current % 2) + 1;

    if (isPlaylistChanged.current) {
      isPlaylistChanged.current = false;
    } else if (!searchedSongRef.current) {
      currentSongIdx.current = findNextValidSong(songOffset.current);
    } else {
      searchedSongRef.current = null;
    }
    songOffset.current = 1;

    fadeElements();
    updateMediaSession(songsRef.current?.[currentSongIdx.current]);
  }, [findNextValidSong, updateMediaSession, fadeElements]);

  // =================== BACKGROUND / FOREGROUND ===================
  // bgAudioRef is a third audio element NOT connected to Web Audio API.
  // It runs MUTED in parallel with the Web Audio elements, always playing
  // the same song at the same position. When iOS backgrounds the page and
  // suspends AudioContext, we just unmute bgAudioRef — zero gap.

  const preSyncBgAudio = useCallback(() => {
    const el = getCurrentAudioRef().current;
    const bgEl = bgAudioRef.current;
    if (!el || !bgEl || !isPlayingRef.current || bgActiveRef.current) return;
    if (!el.src || el.src === window.location.href) return;

    // Start bg element muted if not already running for this song
    if (bgEl.src !== el.src) {
      bgEl.src = el.src;
      bgEl.muted = true;
      bgEl.currentTime = el.currentTime;
      bgEl.play().catch(() => {});
      return;
    }

    // Keep drift under 150ms so transition is imperceptible
    if (!isNaN(el.currentTime) && !isNaN(bgEl.currentTime)) {
      const drift = Math.abs(el.currentTime - bgEl.currentTime);
      if (drift > 0.15) {
        bgEl.currentTime = el.currentTime;
      }
    }

    // Ensure it's still playing muted
    if (bgEl.paused && !bgActiveRef.current) {
      bgEl.muted = true;
      bgEl.play().catch(() => {});
    }
  }, []);

  const activateBgAudio = useCallback(() => {
    const el = getCurrentAudioRef().current;
    const bgEl = bgAudioRef.current;
    if (!el || !bgEl || !isPlayingRef.current) return;
    if (!el.src || el.src === window.location.href) return;

    // Final sync of position
    if (bgEl.src !== el.src) bgEl.src = el.src;
    bgEl.currentTime = el.currentTime;

    // Unmute — bgAudioRef is already playing, so this is instant
    bgEl.muted = false;
    bgActiveRef.current = true;

    // If it somehow wasn't playing, start it
    if (bgEl.paused) {
      bgEl.play().catch(() => { bgActiveRef.current = false; });
    }
  }, []);

  const deactivateBgAudio = useCallback(() => {
    const bgEl = bgAudioRef.current;
    if (!bgEl || !bgActiveRef.current) return;

    const el = getCurrentAudioRef().current;

    // Sync position from bg element back to Web Audio element
    if (el && bgEl.src && bgEl.src !== window.location.href && !isNaN(bgEl.currentTime)) {
      if (el.src !== bgEl.src) el.src = bgEl.src;
      el.currentTime = bgEl.currentTime;
    }

    // Resume Web Audio FIRST, then re-mute bg (keep it playing muted for next time)
    ensureAudioContext();
    setGainValue(getCurrentGainNode(), 1);
    setGainValue(getOtherGainNode(), 0);
    const otherEl = getOtherAudioRef().current;
    if (otherEl && !otherEl.paused) otherEl.pause();

    if (el && isPlayingRef.current) {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          bgEl.muted = true;
          bgActiveRef.current = false;
        }).catch(() => {
          el.load();
          el.play().then(() => {
            bgEl.muted = true;
            bgActiveRef.current = false;
          }).catch(() => {});
        });
      } else {
        bgEl.muted = true;
        bgActiveRef.current = false;
      }
    } else {
      bgEl.muted = true;
      bgActiveRef.current = false;
    }
  }, [ensureAudioContext]);

  // Handle bgAudioRef song ended — advance to next song
  useEffect(() => {
    const bgEl = bgAudioRef.current;
    if (!bgEl) return;
    const handleBgEnded = () => {
      if (!bgActiveRef.current || !isPlayingRef.current) return;
      currentSongIdx.current = findNextValidSong(1);
      songOffset.current = 1;
      const nextUrl = songsRef.current?.[currentSongIdx.current]?.url;
      if (nextUrl) {
        bgEl.src = nextUrl;
        bgEl.play().catch(() => {});
        updateMediaSession(songsRef.current?.[currentSongIdx.current]);
      }
    };
    bgEl.addEventListener('ended', handleBgEnded);
    return () => bgEl.removeEventListener('ended', handleBgEnded);
  }, [findNextValidSong, updateMediaSession]);

  useEffect(() => {
    let bgUnmutedByBlur = false;

    // blur fires BEFORE visibilitychange on iOS — unmute bg early
    const handleBlur = () => {
      if (!isPlayingRef.current) return;
      const bgEl = bgAudioRef.current;
      const el = getCurrentAudioRef().current;
      if (!bgEl || !el) return;
      if (!el.src || el.src === window.location.href) return;

      // Only seek if drift is large — small drift is fine, seeking causes its own delay
      if (bgEl.src === el.src && !isNaN(el.currentTime) && !isNaN(bgEl.currentTime)) {
        const drift = Math.abs(el.currentTime - bgEl.currentTime);
        if (drift > 0.5) {
          bgEl.currentTime = el.currentTime;
        }
      }
      bgEl.muted = false;
      bgUnmutedByBlur = true;
      if (bgEl.paused) bgEl.play().catch(() => {});
    };

    const goBackground = () => {
      if (!isPlayingRef.current) return;
      clearAllFades();
      setGainValue(getCurrentGainNode(), 1);
      setGainValue(getOtherGainNode(), 0);
      const otherEl = getOtherAudioRef().current;
      if (otherEl && !otherEl.paused) otherEl.pause();

      // If blur already unmuted bg, just mark as active
      if (bgUnmutedByBlur) {
        bgActiveRef.current = true;
        bgUnmutedByBlur = false;
      } else {
        activateBgAudio();
      }

      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
        updateMediaSession(songsRef.current?.[currentSongIdx.current]);
      }
    };

    const resumeIfNeeded = () => {
      if (!isPlayingRef.current) return;
      bgUnmutedByBlur = false;

      if (bgActiveRef.current) {
        deactivateBgAudio();
      } else {
        // Re-mute bg in case blur unmuted it but we didn't actually go to bg
        const bgEl = bgAudioRef.current;
        if (bgEl && !bgEl.muted) bgEl.muted = true;

        const el = getCurrentAudioRef().current;
        if (!el || !el.src || el.src === window.location.href) return;

        ensureAudioContext();
        setGainValue(getCurrentGainNode(), 1);
        setGainValue(getOtherGainNode(), 0);
        const otherEl = getOtherAudioRef().current;
        if (otherEl && !otherEl.paused) otherEl.pause();

        if (el.paused) {
          el.play().catch(() => {
            el.load();
            el.play().catch(() => {});
          });
        }
      }

      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = isPlayingRef.current ? 'playing' : 'paused';
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        goBackground();
      } else {
        resumeIfNeeded();
        setTimeout(resumeIfNeeded, 500);
      }
    };
    const handlePageShow = () => { resumeIfNeeded(); setTimeout(resumeIfNeeded, 500); };
    const handleFocus = () => resumeIfNeeded();
    const handlePageHide = () => goBackground();

    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("focus", handleFocus);
    };
  }, [updateMediaSession, clearAllFades, ensureAudioContext, activateBgAudio, deactivateBgAudio]);

  // Media Session handlers are in useMediaSession hook — this effect handles
  // background safety and position state updates only
  useEffect(() => {
    const handleTimeUpdate = (e) => {
      const el = e.target;
      if (el !== getCurrentAudioRef()?.current) return;
      if (!el.duration || isNaN(el.duration)) return;

      if (document.hidden && isPlayingRef.current && !bgActiveRef.current) {
        setGainValue(getCurrentGainNode(), 1);
        const otherEl = getOtherAudioRef()?.current;
        if (otherEl && !otherEl.paused) {
          setGainValue(getOtherGainNode(), 0);
          otherEl.pause();
        }
      }

      if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
        try {
          navigator.mediaSession.setPositionState({
            duration: el.duration,
            playbackRate: el.playbackRate || 1,
            position: Math.min(el.currentTime, el.duration),
          });
        } catch (_) {}
      }
    };

    const handlePlaying = (e) => {
      if (e.target !== getCurrentAudioRef()?.current) return;
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
        updateMediaSession(songsRef.current?.[currentSongIdx.current]);
      }
    };

    const handlePauseEvent = (e) => {
      const el = e.target;
      if (el !== getCurrentAudioRef()?.current) return;
      // Don't auto-resume if bg audio is active (iOS suspended AudioContext)
      if (bgActiveRef.current) return;
      if (isPlayingRef.current && !document.hidden) {
        setTimeout(() => {
          if (isPlayingRef.current && !bgActiveRef.current && el.paused
              && el === getCurrentAudioRef()?.current && !document.hidden) {
            ensureAudioContext();
            el.play().catch(() => {});
          }
        }, 500);
      } else if (!isPlayingRef.current && 'mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    };

    const el1 = audioRef1.current;
    const el2 = audioRef2.current;
    if (el1) { el1.addEventListener('timeupdate', handleTimeUpdate); el1.addEventListener('playing', handlePlaying); el1.addEventListener('pause', handlePauseEvent); }
    if (el2) { el2.addEventListener('timeupdate', handleTimeUpdate); el2.addEventListener('playing', handlePlaying); el2.addEventListener('pause', handlePauseEvent); }
    return () => {
      if (el1) { el1.removeEventListener('timeupdate', handleTimeUpdate); el1.removeEventListener('playing', handlePlaying); el1.removeEventListener('pause', handlePauseEvent); }
      if (el2) { el2.removeEventListener('timeupdate', handleTimeUpdate); el2.removeEventListener('playing', handlePlaying); el2.removeEventListener('pause', handlePauseEvent); }
    };
  }, [updateMediaSession]);

  // =================== ENGINE LIFECYCLE ===================

  const nonUiLifeCycle = useCallback(() => {
    if (document.hidden) return;

    const el = getCurrentAudioRef().current;
    if (!el || !el.src || el.paused || !isPlayingRef.current) return;

    const remaining = el.duration - el.currentTime;
    if (isNaN(remaining)) return;

    // Repeat one: don't crossfade, let the song end naturally and loop
    if (isRepeatRef.current) return;

    if (remaining < FADE_DURATION_SEC && !isFadeOutStarted.current) {
      isFadeOutStarted.current = true;
      startCrossfade();
    }
  }, [startCrossfade]);

  const uiLifeCycle = useCallback(() => {
    if (currentSongIndex !== currentSongIdx.current) {
      setCurrentSongIndex(currentSongIdx.current);
    }
  }, [currentSongIndex, setCurrentSongIndex]);

  // =================== PLAYLIST SWITCH ===================

  const switchPlaylist = useCallback((newSongs, songIndex = 0) => {
    if (!newSongs?.length) return;
    lastSwitchTs.current = Date.now();

    let finalSongs = newSongs;
    let pIdx = songIndex;
    if (isShuffleRef.current) {
      beforeShuffleSongsRef.current = [...newSongs];
      finalSongs = shuffleArray([...newSongs], pIdx);
    }

    songsRef.current = finalSongs;
    currentSongIdx.current = pIdx;
    isPlaylistChanged.current = true;
    isFirstSongOfPlaylist.current = false;

    const currentEl = getCurrentAudioRef().current;
    const otherEl = getOtherAudioRef().current;

    const hasActiveSong = currentEl?.src && currentEl.src !== window.location.href
      && !currentEl.paused && currentEl.duration > 0 && !isNaN(currentEl.duration);

    ensureAudioContext();

    if (hasActiveSong) {
      otherEl.src = finalSongs[pIdx]?.url;
      currentAudioRefIndex.current = (currentAudioRefIndex.current % 2) + 1;
      isPlaylistChanged.current = false;
      songOffset.current = 1;
      fadeElements();
    } else {
      // First play — no active song yet
      currentEl.src = finalSongs[pIdx]?.url;
      setGainValue(getCurrentGainNode(), 1);
      setGainValue(getOtherGainNode(), 0);
      isPlayingRef.current = true;

      if (otherEl && !otherEl.paused) otherEl.pause();

      const p = currentEl.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          currentEl.addEventListener("canplay", () => {
            currentEl.play().catch(() => {});
          }, { once: true });
          currentEl.load();
        });
      }
    }

    updateMediaSession(finalSongs[pIdx]);
    return finalSongs;
  }, [findNextValidSong, updateMediaSession, fadeElements, ensureAudioContext]);

  const shuffleInPlace = useCallback((newSongs, currentIndex) => {
    lastSwitchTs.current = Date.now();
    songsRef.current = newSongs;
    currentSongIdx.current = currentIndex;
    if (!isFadeOutStarted.current) {
      const nextUrl = newSongs?.[findNextValidSong(1) % size(newSongs)]?.url;
      if (nextUrl && getOtherAudioRef()?.current) getOtherAudioRef().current.src = nextUrl;
    }
  }, [findNextValidSong]);

  // =================== EXPOSE ENGINE GLOBALLY ===================

  useEffect(() => {
    window.__4playEngine = {
      getCurrentAudioRef,
      getOtherAudioRef,
      getCurrentGain: getCurrentGainNode,
      getOtherGain: getOtherGainNode,
      setGain,
      setGainValue,
      volume: { current: 1 },
      currentAudioRefIndex,
      currentSongIdx,
      isFadeOutStarted,
      isTimeChanging: { current: false },
      isVolumeChanging: { current: false },
      songOffset,
      isPlaylistChanged,
      findNextValidSong,
      primeElements,
      killAllFades: clearAllFades,
      switchPlaylist,
      shuffleInPlace,
      setBeforeShuffleSongs: (s) => { beforeShuffleSongsRef.current = s; },
      getBeforeShuffleSongs: () => beforeShuffleSongsRef.current,
      playCurrent: () => {
        const el = getCurrentAudioRef().current;
        if (el) {
          ensureAudioContext();
          setGainValue(getCurrentGainNode(), 1);
          safePlay(el);
        }
      },
    };
    return () => { delete window.__4playEngine; };
  }, [findNextValidSong, primeElements, switchPlaylist, shuffleInPlace, clearAllFades, ensureAudioContext]);

  // Main cycle timer
  useEffect(() => {
    cycleTimerRef.current = setInterval(() => {
      nonUiLifeCycle();
      uiLifeCycle();
      preSyncBgAudio();
    }, 500);
    return () => clearInterval(cycleTimerRef.current);
  }, [nonUiLifeCycle, uiLifeCycle, preSyncBgAudio]);

  // Song ended + stalled recovery
  useEffect(() => {
    const handleEnded = () => {
      if (isRepeatRef.current) {
        const el = getCurrentAudioRef().current;
        if (el) { el.currentTime = 0; safePlay(el); }
        return;
      }
      if (isFadeOutStarted.current) return;
      isFadeOutStarted.current = true;
      startCrossfade();
    };

    const makeHandleStalled = (ref) => () => {
      const el = ref.current;
      if (!el || el !== getCurrentAudioRef().current) return;
      if (!isPlayingRef.current) return;
      if (!el.src || el.src === window.location.href) return;
      const t = el.currentTime;
      el.load();
      el.currentTime = t;
      el.play().catch(() => {});
    };

    const el1 = audioRef1.current;
    const el2 = audioRef2.current;
    const stall1 = makeHandleStalled(audioRef1);
    const stall2 = makeHandleStalled(audioRef2);
    if (el1) { el1.addEventListener("ended", handleEnded); el1.addEventListener("stalled", stall1); }
    if (el2) { el2.addEventListener("ended", handleEnded); el2.addEventListener("stalled", stall2); }
    return () => {
      if (el1) { el1.removeEventListener("ended", handleEnded); el1.removeEventListener("stalled", stall1); }
      if (el2) { el2.removeEventListener("ended", handleEnded); el2.removeEventListener("stalled", stall2); }
    };
  }, [startCrossfade]);

  // Set audio refs in store
  useEffect(() => {
    setAudioRef1(audioRef1.current);
    setAudioRef2(audioRef2.current);
    setCurrentAudioRefIndex(currentAudioRefIndex);
  }, [setAudioRef1, setAudioRef2, setCurrentAudioRefIndex]);

  // Songs/playlist changed externally
  useEffect(() => {
    songsRef.current = songs;

    if (Date.now() - lastSwitchTs.current < 2000) return;

    const currentAudioRef = getCurrentAudioRef();
    if (!audioRef1.current || isEmpty(songs)) return;

    ensureAudioContext();

    if (currentAudioRef.current?.src && currentAudioRef.current.src !== "" && currentAudioRef.current.src !== window.location.href) {
      const playlistSongIndex = playList?.songIndex || 0;
      currentSongIdx.current = playlistSongIndex;
      isPlaylistChanged.current = true;

      getOtherAudioRef().current.src = songs[playlistSongIndex]?.url;
      if (isPlayingRef.current) {
        currentAudioRefIndex.current = (currentAudioRefIndex.current % 2) + 1;
        isPlaylistChanged.current = false;
        fadeElements();
        updateMediaSession(songs[playlistSongIndex]);
      } else {
        currentAudioRef.current.src = songs[playlistSongIndex]?.url;
      }
      return;
    }

    // First load
    getCurrentAudioRef().current.src = songs?.[currentSongIdx.current]?.url;
    getOtherAudioRef().current.src = songs?.[findNextValidSong(1) % size(songs)]?.url;
    setGainValue(getCurrentGainNode(), 1);
    setGainValue(getOtherGainNode(), 0);
    if (isPlayingRef.current) {
      safePlay(getCurrentAudioRef().current);
      isFirstSongOfPlaylist.current = false;
    } else {
      isFirstSongOfPlaylist.current = true;
    }
  }, [songs, playList]);

  // Song index changed externally → crossfade
  useEffect(() => {
    if (currentSongIndex === currentSongIdx.current) return;

    songOffset.current = currentSongIndex - currentSongIdx.current;
    const el = getCurrentAudioRef().current;
    if (!el || !el.src) return;

    const nextUrl = songsRef.current?.[findNextValidSong(songOffset.current) % size(songsRef.current)]?.url;
    if (nextUrl && getOtherAudioRef()?.current) getOtherAudioRef().current.src = nextUrl;

    isFadeOutStarted.current = true;
    startCrossfade();
  }, [currentSongIndex]);

  // Play/pause
  useEffect(() => {
    isPlayingRef.current = isPlaying;
    const currentAudioRef = getCurrentAudioRef();
    ensureAudioContext();

    if (!isPlaying) {
      clearAllFades();
      if (audioRef1.current && !audioRef1.current.paused) audioRef1.current.pause();
      if (audioRef2.current && !audioRef2.current.paused) audioRef2.current.pause();
      if (bgAudioRef.current) { bgAudioRef.current.pause(); bgAudioRef.current.muted = true; bgActiveRef.current = false; }
      setGainValue(gainNode1Ref, 0);
      setGainValue(gainNode2Ref, 0);
    } else {
      if (!currentAudioRef.current?.src || currentAudioRef.current.src === "" || currentAudioRef.current.src === window.location.href) return;
      // If a crossfade is in progress, don't interfere
      if (Date.now() - lastSwitchTs.current < 500 && !currentAudioRef.current.paused) return;
      ensureAudioContext();
      setGainValue(getCurrentGainNode(), 1);
      setGainValue(getOtherGainNode(), 0);
      safePlay(currentAudioRef.current);
      isFirstSongOfPlaylist.current = false;
      updateMediaSession(songsRef.current?.[currentSongIdx.current]);
    }
  }, [isPlaying, updateMediaSession, clearAllFades, ensureAudioContext]);

  // Shuffle
  useEffect(() => {
    if (isShuffle !== isShuffleRef.current) {
      isShuffleRef.current = isShuffle;
    }
  }, [isShuffle]);

  // Repeat
  useEffect(() => {
    isRepeatRef.current = isRepeat;
  }, [isRepeat]);

  // Search song
  useEffect(() => {
    searchedSongRef.current = searchedSong;
    if (!searchedSong?.url) return;
    getOtherAudioRef().current.src = searchedSong.url;
    isFadeOutStarted.current = true;
    startCrossfade();
  }, [searchedSong]);

  // Auth change
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllFades();
      if (audioRef1.current && !audioRef1.current.paused) audioRef1.current.pause();
      if (audioRef2.current && !audioRef2.current.paused) audioRef2.current.pause();
    }
  }, [isAuthenticated, clearAllFades]);

  // Schedule
  const scheduleCycle = useCallback(() => {
    if (!playlistsRef.current?.length) return;
    const now = new Date();
    const day = now.getDay();
    const time = now.toLocaleTimeString("en-US", { hour12: false, hour: "numeric", minute: "numeric" });
    for (const pl of playlistsRef.current) {
      if (!pl.schedule?.length) continue;
      for (const s of pl.schedule) {
        if (s.days?.includes(day) && time === s.time) {
          const uid = `${pl.id}-${day}-${s.time}`;
          if (!scheduleDoneRef.current[uid]) {
            setSongs(pl.songs);
            setPlayList({ id: pl.id });
            setIsPlaying(true);
            navigate("/mobile");
            markScheduleDone(uid);
            return;
          }
        }
      }
    }
  }, [setSongs, setPlayList, setIsPlaying, navigate, markScheduleDone]);

  // Token refresh & schedule timers
  useEffect(() => {
    tokenTimerRef.current = setInterval(() => {
      if (!userOffline) doTokenRefresh().then(() => {
        if (socketRef.current) { socketRef.current.disconnect(); connectSocket(); }
      });
    }, 8 * 60 * 1000);
    const schedTimer = setInterval(scheduleCycle, 1000);
    return () => { clearInterval(tokenTimerRef.current); clearInterval(schedTimer); };
  }, [userOffline, scheduleCycle]);

  // Socket
  const connectSocket = useCallback(() => {
    socketRef.current = io(BASE_URL, { reconnect: true, transports: ["websocket"], query: { token: getToken() } });
    socketRef.current.on("connect", () => {
      socketRef.current.on("message", (data) => {
        const { type } = data;
        if (type === "shuffle") {
          if (!!data.data.shuffle !== isShuffleRef.current) toggleIsShuffle(!!data.data.shuffle);
        } else if (type === "song_changed") {
          const { playlist_id, song_id, song_name } = data.data;
          const pl = playlistsRef.current.find((p) => p.id === playlist_id);
          if (pl) {
            const filtered = pl.songs.filter((s) => !s.ignore);
            let idx = filtered.findIndex((s) => s.id === song_id);
            if (idx === -1) idx = 0;
            if (pl.id !== playListRef.current?.id) {
              setPlayListFromScoket({ id: pl.id, songIndex: idx }, pl.songs, idx);
            } else if (idx !== currentSongIdx.current) {
              setCurrentSongIndex(idx);
            } else {
              setIsPlaying(!isPlayingRef.current);
            }
          } else {
            playSearchSong({ id: song_id, title: song_name, fileName: song_id + ".mp3" });
          }
        } else if (type === "volume_changed") {
          // Mobile: volume controlled by hardware
        } else if (type === "pause") {
          if (isPlayingRef.current) setIsPlaying(false);
        } else if (type === "song_liked") {
          const { song_id, status, song_name, artist_name } = data?.data || {};
          const songObj = { id: song_id, title: song_name, artist: artist_name, fileName: song_id + ".mp3", imageFileName: song_id + ".jpg", index: "0.00", ignore: false, favorite: true, url: MEDIA_URL + "/songs/" + song_id + ".mp3", color: getRandomColor() };
          if (!status) { deleteLikedSong(song_id); removeUnLikedSongFromPlaylist(songObj); }
          else { addLikedSong(song_id); addLikedSongToPlaylist(songObj); }
        } else if (type === "refresh_playlist") {
          if (!userOffline) fetchCustomerData().then((d) => { if (d?.playlists) setPlaylists(d.playlists); });
        } else if (type === "song_ignored") {
          const { song_id, status, playlist_id } = data?.data || {};
          if (status) addIgnoreSong(song_id + playlist_id);
          else removeIgnoreSong(song_id + playlist_id);
        }
      });
    });
  }, []);

  useEffect(() => {
    if (userOffline) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      return;
    }
    connectSocket();
    return () => { if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; } };
  }, [isAuthenticated, userOffline, connectSocket]);

  // Emit song change
  useEffect(() => {
    const payload = {};
    if (!isEmpty(songs)) payload.song_id = songs?.[currentSongIndex]?.id;
    if (playList) payload.playlist_id = playList?.id;
    if (searchedSong) { payload.song_id = searchedSong?.id; payload.song_name = searchedSong?.title; delete payload.playlist_id; }
    if (!isEmpty(payload) && !userOffline) {
      try { socketServices.changeSong(payload); } catch (_) {}
    }
  }, [currentSongIndex, songs, playList, searchedSong, userOffline]);

  // Sync refs
  useEffect(() => { playlistsRef.current = playlists; }, [playlists]);
  useEffect(() => { scheduleDoneRef.current = schedulesDone; }, [schedulesDone]);
  useEffect(() => { playListRef.current = playList; }, [playList]);

  const hiddenStyle = { position: "absolute", top: 0, left: 0, width: "1px", height: "1px", opacity: 0, pointerEvents: "none", zIndex: -1 };
  return (
    <>
      <audio ref={audioRef1} style={hiddenStyle} preload="auto" playsInline crossOrigin="anonymous" />
      <audio ref={audioRef2} style={hiddenStyle} preload="auto" playsInline crossOrigin="anonymous" />
      {/* Background audio: NOT connected to Web Audio API — plays natively on iOS in background */}
      <audio ref={bgAudioRef} style={hiddenStyle} preload="auto" playsInline />
    </>
  );
};

export default MusicPlayerEngine;
